// NotificationBell.js - Complete notification system component
import React, { useState, useEffect, useRef } from 'react';
import {
  Bell,
  X,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Calendar,
  DollarSign,
  ArrowRight,
  Check,
  Eye,
  Send,
  MessageSquare,
  AlertCircle,
  Timer,
  Briefcase,
  TrendingUp,
  UserCheck,
  ClipboardList,
  Target,
  Zap,
  Filter,
  MoreHorizontal
} from 'lucide-react';
import { useNotifications, useNotificationActions } from '../hooks/useNotifications';

// Notification priority and type configurations
const getNotificationConfig = (type, priority) => {
  const configs = {
    timesheet_reminder: {
      icon: Clock,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      category: 'Time Tracking'
    },
    approval_pending: {
      icon: UserCheck,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      category: 'Approvals'
    },
    invoice_ready: {
      icon: DollarSign,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      category: 'Billing'
    },
    deadline_warning: {
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      category: 'Projects'
    },
    team_update: {
      icon: Users,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      category: 'Team'
    },
    overtime_alert: {
      icon: TrendingUp,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      category: 'Workload'
    }
  };

  const priorityColors = {
    high: 'border-l-red-500',
    medium: 'border-l-yellow-500',
    low: 'border-l-green-500'
  };

  return {
    ...configs[type],
    priorityColor: priorityColors[priority] || 'border-l-gray-300'
  };
};

// Individual notification item component
const NotificationItem = ({ notification, onMarkAsRead, onDelete, onAction }) => {
  const config = getNotificationConfig(notification.type, notification.priority);
  const IconComponent = config.icon;

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleAction = (actionType) => {
    onAction?.(actionType, notification);
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
  };

  // Parse action data
  const actionData = typeof notification.action_data === 'string' 
    ? JSON.parse(notification.action_data) 
    : notification.action_data || {};

  // Define actions based on notification type
  const getActions = () => {
    switch (notification.type) {
      case 'timesheet_reminder':
        return [
          { type: 'submit_timesheet', label: 'Submit Now', variant: 'primary' },
          { type: 'view_entries', label: 'View Entries', variant: 'secondary' }
        ];
      case 'approval_pending':
        return [
          { type: 'review_entries', label: 'Review All', variant: 'primary' },
          { type: 'bulk_approve', label: 'Quick Approve', variant: 'secondary' }
        ];
      case 'invoice_ready':
        return [
          { type: 'generate_invoice', label: 'Generate Invoice', variant: 'primary' },
          { type: 'view_details', label: 'View Details', variant: 'secondary' }
        ];
      case 'deadline_warning':
        return [
          { type: 'view_project', label: 'View Project', variant: 'primary' },
          { type: 'add_time', label: 'Log Time', variant: 'secondary' }
        ];
      case 'team_update':
        return [
          { type: 'view_profile', label: 'View Profile', variant: 'secondary' }
        ];
      case 'overtime_alert':
        return [
          { type: 'review_schedule', label: 'Review Schedule', variant: 'primary' },
          { type: 'contact_user', label: `Contact ${actionData.userName?.split(' ')[0]}`, variant: 'secondary' }
        ];
      default:
        return [];
    }
  };

  const actions = getActions();

  return (
    <div className={`
      relative border-l-4 ${config.priorityColor} ${config.bgColor} ${config.borderColor}
      border border-l-4 rounded-lg p-4 mb-3 transition-all duration-200 hover:shadow-md
      ${!notification.is_read ? 'ring-2 ring-blue-100' : ''}
    `}>
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full"></div>
      )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className={`p-2 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
          <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Category and priority */}
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {config.category}
                </span>
                <span className={`
                  px-1.5 py-0.5 text-xs font-medium rounded-full
                  ${notification.priority === 'high' ? 'bg-red-100 text-red-700' : ''}
                  ${notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : ''}
                  ${notification.priority === 'low' ? 'bg-green-100 text-green-700' : ''}
                `}>
                  {notification.priority}
                </span>
              </div>

              {/* Title and message */}
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {notification.title}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {notification.message}
              </p>

              {/* Action data display */}
              {actionData && Object.keys(actionData).length > 0 && (
                <div className="bg-white/50 rounded-md p-2 mb-3 space-y-1">
                  {notification.type === 'approval_pending' && (
                    <>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">{actionData.pendingCount} entries</span> • 
                        <span className="ml-1">{actionData.totalHours} hours</span>
                      </div>
                      {actionData.users && (
                        <div className="text-xs text-gray-500">
                          From: {actionData.users.slice(0, 2).join(', ')}
                          {actionData.users.length > 2 && ` +${actionData.users.length - 2} more`}
                        </div>
                      )}
                    </>
                  )}
                  {notification.type === 'deadline_warning' && (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">{actionData.completion}% complete</span> • 
                      <span className="ml-1">{actionData.hoursRemaining}h remaining</span>
                    </div>
                  )}
                  {notification.type === 'invoice_ready' && (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">${actionData.amount?.toLocaleString()}</span> • 
                      <span className="ml-1">{actionData.billableHours} billable hours</span>
                    </div>
                  )}
                  {notification.type === 'timesheet_reminder' && (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">{actionData.entryCount} entries</span> • 
                      <span className="ml-1">{actionData.totalHours} hours</span>
                    </div>
                  )}
                  {notification.type === 'overtime_alert' && (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">{actionData.hoursWorked}h worked</span> • 
                      <span className="ml-1">{actionData.overtimeHours}h overtime</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {actions && actions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {actions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleAction(action.type)}
                      className={`
                        inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                        ${action.variant === 'primary' 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      {action.label}
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Delete button */}
            <button
              onClick={() => onDelete(notification.id)}
              className="text-gray-400 hover:text-gray-600 p-1"
              title="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Timestamp */}
          <div className="text-xs text-gray-500 mt-2">
            {formatTimeAgo(notification.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Notifications dropdown component
const NotificationsDropdown = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { handleAction } = useNotificationActions();
  const [filter, setFilter] = useState('all'); // all, unread, high-priority
  const dropdownRef = useRef(null);

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    if (filter === 'high-priority') return notification.priority === 'high';
    return true;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 top-12 w-96 max-h-96 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter buttons */}
        <div className="flex space-x-1">
          {[
            { key: 'all', label: 'All', count: notifications.length },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'high-priority', label: 'High Priority', count: notifications.filter(n => n.priority === 'high').length }
          ].map(filterOption => (
            <button
              key={filterOption.key}
              onClick={() => setFilter(filterOption.key)}
              className={`
                px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${filter === filterOption.key 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              {filterOption.label} ({filterOption.count})
            </button>
          ))}
        </div>
      </div>

      {/* Notifications list */}
      <div className="max-h-80 overflow-y-auto p-4">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No notifications to show</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDelete={deleteNotification}
                onAction={handleAction}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main notification bell component
const NotificationBell = () => {
  const { unreadCount } = useNotifications();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationsDropdown 
        isOpen={isDropdownOpen} 
        onClose={() => setIsDropdownOpen(false)} 
      />
    </div>
  );
};

export default NotificationBell;